import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { useUtilityProofStore } from '../../stores/utility-proof.store';
import { UtilityType } from '../../types/utility-proof.type';
import { listUtilityCatalog } from '../../services/utility-emission.service';
import { UtilityActivityType } from '../../types/utility-emission.type';

export interface UtilitySettingsPanelProps {
  siteId: string | null;
}

interface SettingsDraft {
  utilities_not_required: boolean;
  required_utilities: UtilityType[];
  lock_after_days: string;
}

const DEFAULT_DRAFT: SettingsDraft = {
  utilities_not_required: false,
  required_utilities: [],
  lock_after_days: '7',
};

const UtilitySettingsPanel: React.FC<UtilitySettingsPanelProps> = ({ siteId }) => {
  const {
    settings,
    isLoadingSettings,
    isSavingSettings,
    settingsError,
    clearSettingsError,
    fetchSettings,
    saveSettings,
  } = useUtilityProofStore();

  const [draft, setDraft] = useState<SettingsDraft>(DEFAULT_DRAFT);
  const [catalog, setCatalog] = useState<UtilityActivityType[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!siteId) return;
    void fetchSettings(siteId);
    void listUtilityCatalog().then(setCatalog);
  // fetchSettings is a stable zustand action
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  useEffect(() => {
    if (settings) {
      setDraft({
        utilities_not_required: settings.utilities_not_required,
        required_utilities: [...(settings.required_utilities ?? [])],
        lock_after_days: String(settings.lock_after_days ?? ''),
      });
    }
  }, [settings]);

  const handleToggleNotRequired = (checked: boolean) => {
    setDraft((d) => ({ ...d, utilities_not_required: checked }));
  };

  const handleToggleUtility = (type: UtilityType, checked: boolean) => {
    setDraft((d) => ({
      ...d,
      required_utilities: checked
        ? [...d.required_utilities, type]
        : d.required_utilities.filter((u) => u !== type),
    }));
  };

  const handleLockDaysChange = (value: string) => {
    setDraft((d) => ({ ...d, lock_after_days: value }));
  };

  const handleSave = useCallback(async () => {
    if (!siteId) return;

    const lockDays = parseInt(draft.lock_after_days, 10);
    if (!Number.isFinite(lockDays) || lockDays < 0) {
      setLocalError('Lock after days must be a non-negative whole number.');
      return;
    }
    if (!draft.utilities_not_required && draft.required_utilities.length === 0) {
      setLocalError(
        'Select at least one required utility, or enable "Utilities tracking not required".'
      );
      return;
    }

    setLocalError(null);
    const ok = await saveSettings(siteId, {
      utilities_not_required: draft.utilities_not_required,
      required_utilities: draft.utilities_not_required ? [] : draft.required_utilities,
      lock_after_days: lockDays,
    });
    if (ok) setSuccessMsg('Settings saved successfully.');
  }, [siteId, draft, saveSettings]);

  if (!siteId) {
    return (
      <Alert severity="info" icon={<StorefrontOutlinedIcon fontSize="inherit" />}>
        Select a branch to configure utility settings.
      </Alert>
    );
  }

  const disabled = isLoadingSettings || isSavingSettings;
  const hasError = Boolean(settingsError || localError);

  return (
    <Box sx={{ maxWidth: 520 }}>
      {isLoadingSettings && <LinearProgress sx={{ mb: 2 }} />}

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
        Utility Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure which utilities this branch must track and when monthly records are locked.
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {/* Exempt toggle */}
      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={draft.utilities_not_required}
              onChange={(e) => handleToggleNotRequired(e.target.checked)}
              disabled={disabled}
            />
          }
          label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Utilities tracking not required
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Exempt this branch from uploading utility proof documents.
              </Typography>
            </Box>
          }
          sx={{ alignItems: 'flex-start', ml: 0 }}
        />
      </Box>

      {/* Required utilities checkboxes — hidden when exempt */}
      {!draft.utilities_not_required && (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Required utilities
          </Typography>
          <FormGroup sx={{ mb: 3, pl: 0.5 }}>
            {catalog.map((utilityType) => (
              <FormControlLabel
                key={utilityType.key}
                control={
                  <Checkbox
                    checked={draft.required_utilities.includes(utilityType.key)}
                    onChange={(e) => handleToggleUtility(utilityType.key, e.target.checked)}
                    disabled={disabled}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{utilityType.label}</Typography>}
              />
            ))}
          </FormGroup>
        </>
      )}

      {/* Lock grace period */}
      <Box sx={{ mb: 3 }}>
        <TextField
          label="Lock after days"
          type="number"
          size="small"
          value={draft.lock_after_days}
          onChange={(e) => handleLockDaysChange(e.target.value)}
          disabled={disabled}
          inputProps={{ min: 0, step: 1 }}
          sx={{ width: 200 }}
          helperText="Days after month end before records auto-lock."
        />
      </Box>

      <Button
        variant="contained"
        size="medium"
        startIcon={<SaveOutlinedIcon />}
        onClick={() => void handleSave()}
        disabled={disabled}
      >
        {isSavingSettings ? 'Saving…' : 'Save Settings'}
      </Button>

      {/* Error snackbar */}
      <Snackbar
        open={hasError}
        autoHideDuration={6000}
        onClose={() => {
          clearSettingsError();
          setLocalError(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => {
            clearSettingsError();
            setLocalError(null);
          }}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {settingsError || localError}
        </Alert>
      </Snackbar>

      {/* Success snackbar */}
      <Snackbar
        open={Boolean(successMsg)}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessMsg(null)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {successMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UtilitySettingsPanel;
